import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../lib/api';
import { motion } from 'framer-motion';
import { staggerContainer, shameShake } from '../lib/animations';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import AnimatedButton from '../components/AnimatedButton';

interface ShameEntry {
  id: number;
  roller_name: string;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
  roll_consistency: number;
  shape_taper: number;
  filter_crutch: number;
  seal_quality: number;
  aesthetics: number;
}

const dimensions = [
  { key: 'roll_consistency', label: 'Roll Consistency' },
  { key: 'shape_taper', label: 'Shape & Taper' },
  { key: 'filter_crutch', label: 'Filter / Crutch' },
  { key: 'seal_quality', label: 'Seal Quality' },
  { key: 'aesthetics', label: 'Aesthetics' },
] as const;

export default function HallOfShame() {
  const { toasts, addToast } = useToast();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<ShameEntry[]>([]);
  const [expandedImg, setExpandedImg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(getApiUrl('/api/hall-of-shame'))
      .then(r => {
        if (!r.ok) throw new Error('Failed to load hall of shame');
        return r.json();
      })
      .then(data => {
        setEntries(Array.isArray(data) ? data : []);
        setLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load hall of shame:', err);
        setError('Failed to load hall of shame');
        addToast('Failed to load hall of shame', 'error');
        setLoaded(true);
      });
  }, [addToast]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-red-400 mb-2">Hall of Shame</h2>
        <p className="text-red-300/70 text-sm">These joints should never have been rolled</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {!loaded ? (
        <p className="text-cream/40 text-center py-8">Loading...</p>
      ) : error ? (
        <p className="text-cream/40 text-center py-8">Unable to load hall of shame</p>
      ) : entries.length === 0 ? (
        <p className="text-cream/40 text-center py-8">No ratings yet.</p>
      ) : (
        <motion.div className="space-y-4" variants={staggerContainer} initial="initial" animate="animate">
          {entries.map((entry, i) => (
            <motion.div
              key={entry.id}
              variants={shameShake}
              className="bg-cannabis-800 rounded-xl p-5 border border-red-900/50"
            >
              <div className="flex items-start gap-4">
                <div className="text-center shrink-0">
                  <div className="text-2xl"></div>
                  <div className="text-red-400 font-bold text-lg">#{i + 1}</div>
                </div>

                {entry.image_path && (
                  <img
                    src={entry.image_path}
                    alt="Shameful joint"
                    className="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition shrink-0"
                    onClick={() => setExpandedImg(entry.image_path)}
                  />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-cream font-semibold">{entry.roller_name}</span>
                    <span className="text-cream/30 text-xs">{new Date(entry.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-red-400 font-mono font-bold text-xl mb-1">
                    {entry.final_score.toFixed(2)} / 10
                  </div>
                  <p className="text-cream/60 italic text-xs mb-3 line-clamp-2">"{entry.verdict}"</p>

                  <div className="space-y-1.5">
                    {dimensions.map(d => (
                      <div key={d.key} className="flex items-center gap-2 text-xs">
                        <span className="text-cream/40 w-28 shrink-0">{d.label}</span>
                        <div className="flex-1 bg-cannabis-900 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-red-500/80 rounded-full transition-all duration-700"
                            style={{ width: `${(entry[d.key] / 10) * 100}%`, transitionDelay: `${i * 80 + 200}ms` }}
                          />
                        </div>
                        <span className="text-red-400/70 font-mono w-8 text-right">{entry[d.key].toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      <div className="text-center mt-10">
        <AnimatedButton onClick={() => navigate('/rate')} variant="danger">
          Think you can do worse?
        </AnimatedButton>
      </div>

      {expandedImg && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setExpandedImg(null)}
        >
          <img src={expandedImg} alt="Full size" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
      <Toast toasts={toasts} />
    </div>
  );
}
